import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApiConstants } from '@zlib/config/api.constants';
import { UtilsService } from '@zlib/services/utils.services';
import { AppInjector } from '@zshared/utilities/injector';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { CookieService } from './cookie.services';
import { LocalStorageService } from './localstorage.service';
import { KeycloakService } from 'keycloak-angular';

@Injectable({ providedIn: 'root' })
export class BaseApiService {
    public apiUrl: string;
    public notificationErrorTitle = '';
    public loading: boolean;
    public http: HttpClient;
    public notification: NzNotificationService;
    public translate: TranslateService;
    public router: Router;
    public cookieService: CookieService;
    public localStorageService: LocalStorageService;
    public keycloak: KeycloakService;

    public utilsService: UtilsService;
    constructor() {
        this.apiUrl = ApiConstants.URL;
        this.loading = false;
        this.http = AppInjector.getService<HttpClient>(HttpClient);
        this.notification = AppInjector.getService<NzNotificationService>(
            NzNotificationService
        );
        this.translate =
            AppInjector.getService<TranslateService>(TranslateService);
        this.router = AppInjector.getService<Router>(Router);
        this.cookieService =
            AppInjector.getService<CookieService>(CookieService);
        this.localStorageService =
            AppInjector.getService<LocalStorageService>(LocalStorageService);
        this.utilsService = AppInjector.getService<UtilsService>(UtilsService);
        this.keycloak =
            AppInjector.getService<KeycloakService>(KeycloakService);
    }

    public get<T>(
        endPoint: string,
        params?:
            | HttpParams
            | {
                  [param: string]:
                      | string
                      | number
                      | boolean
                      | ReadonlyArray<string | number | boolean>;
              }
    ): Observable<T> {
        const url = `${this.apiUrl}/${endPoint}`;
        return this.http
            .get<T>(`${url}`, { params: params })
            .pipe(catchError(this._handleErrorException.bind(this)));
    }

    public getBlob(
        endPoint: string,
        params?:
            | HttpParams
            | {
                  [param: string]: string | string[];
              }
    ): Observable<Blob> {
        const url = `${this.apiUrl}/${endPoint}`;
        return this.http
            .get(`${url}`, { params, responseType: 'blob' })
            .pipe(catchError(this._handleErrorException.bind(this)));
    }

    public postBlob(
        endPoint: string,
        data: any,
        params?:
            | HttpParams
            | {
                  [param: string]: string | string[];
              }
    ): Observable<Blob> {
        const url = `${this.apiUrl}/${endPoint}`;
        return this.http
            .post(`${url}`, data, { params, responseType: 'blob' })
            .pipe(catchError(this._handleErrorException.bind(this)));
    }

    public post<T>(
        endPoint: string,
        data: any,
        options?: {
            params?:
                | HttpParams
                | {
                      [param: string]:
                          | string
                          | number
                          | boolean
                          | ReadonlyArray<string | number | boolean>;
                  };
            reportProgress?: boolean;
            responseType: any;
            withCredentials?: boolean;
        }
    ): Observable<T> {
        const url = `${this.apiUrl}/${endPoint}`;
        return this.http
            .post<T>(`${url}`, data, options)
            .pipe(
                map(this._interceptResponseResult),
                catchError(this._handleErrorException.bind(this))
            );
    }

    public put<T>(
        endPoint: string,
        data: any,
        params?:
            | HttpParams
            | {
                  [param: string]: any | any[];
              }
    ): Observable<T> {
        const lang = this.getLang();
        const url = `${this.apiUrl}/${endPoint}`;
        let headers = new HttpHeaders();
        headers = headers.set('Accept-Language', lang);
        return this.http
            .put<T>(`${url}`, data, {
                headers,
                params: params
            })
            .pipe(
                map(this._interceptResponseResult),
                catchError(this._handleErrorException.bind(this))
            );
    }

    public delete<T>(
        endPoint: string,
        params?:
            | HttpParams
            | {
                  [param: string]: string | string[];
              }
    ): Observable<T> {
        const lang = this.getLang();
        const url = `${this.apiUrl}/${endPoint}`;
        let headers = new HttpHeaders();
        headers = headers.set('Accept-Language', lang);
        return this.http
            .delete<T>(`${url}`, { headers, params })
            .pipe(catchError(this._handleErrorException.bind(this)));
    }

    public getToken(): string {
        return this.localStorageService.loadInfo('token');
    }

    public getLang(): string {
        return this.cookieService.get('lang');
    }

    public setNotificationErrorTitle(title: string) {
        this.notificationErrorTitle = title;
    }

    public getTranslate(key: string, params?: Object) {
        return this.translate.instant(key, params);
    }

    public makeFormData(params: any) {
        const formData = new FormData();
        for (const prop in params) {
            if (params[prop] === null || params[prop] === undefined) {
                continue;
            }
            if (Array.isArray(params[prop])) {
                const arr = params[prop] as string[];

                arr.forEach((value) => {
                    formData.append(prop, value);
                });
            } else {
                formData.append(prop, params[prop]);
            }
        }
        return formData;
    }

    public postProgress<T>(
        endPoint: string,
        data: any,
        params?:
            | HttpParams
            | {
                  [param: string]: string | string[];
              }
    ): Observable<T> {
        const url = `${this.apiUrl}/${endPoint}`;
        return this.http
            .post<T>(`${url}`, data, {
                params,
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                map(this._interceptResponseResult),
                catchError(this._handleErrorException.bind(this))
            );
    }

    public head(url: string) {
        return this.http.head(url);
    }

    private _interceptResponseResult(response: any) {
        return response;
    }
    private _handleErrorException = (errorResponse: HttpErrorResponse) => {
        let errorMsg: string;
        if (errorResponse.error instanceof ErrorEvent) {
            errorMsg = `Error: ${errorResponse.error.message}`;
        } else {
            errorMsg = this._getServerErrorMessage(errorResponse);
        }

        if (errorMsg) {
            console.error(errorMsg);
        }
        return throwError(errorMsg ? this.translate.instant(errorMsg) : '');
    };

    private _getServerErrorMessage(errorResponse: HttpErrorResponse): string {
        const message =
            errorResponse?.error?.error ||
            errorResponse?.error?.message ||
            errorResponse?.message;

        switch (errorResponse.status) {
            case 403:
            case 401:
                if (
                    message.indexOf('error.login_fail') !== -1 ||
                    message.indexOf('error.account_inactive') !== -1
                ) {
                    const parts = message.split('_');
                    const loginFailCount = parts[2] ? parts[2] : null;
                    const maxLoginFail = parts[3] ? parts[3] : null;
                    if (loginFailCount != null && maxLoginFail != null) {
                        return this.translate.instant('error.login_fail', {
                            max_login_fail: maxLoginFail,
                            login_remain: maxLoginFail - loginFailCount
                        });
                    } else {
                        return this.translate.instant('error.account_lock');
                    }
                }
                /** clear all storage */
                this.localStorageService.clearAllUserStorage();
                this.keycloak.login();
                return '';
            case 500: {
                return `${message}`;
            }
            default: {
                if (errorResponse?.error?.message) {
                    return `${this.translate.instant(errorResponse.error.message)}`;
                }
                return `${this.translate.instant(
                    'request.unknown_server_error'
                )}`;
            }
        }
    }
}
